import { useEffect, useRef } from 'react'
import semver from 'semver'
import useRoomContextRef from './useRoomContextRef'
import { stripSemverVersion } from '../utils'
import { MissingRemoteDevice } from '../RoomContext'
import { AudioInputStreamInfo } from '../UserContext'
import { MusePeer } from './RoomContextMusePeer'
import { logger } from './Logger'
import { videoSubscriptionManager } from './VideoSubscriptionManager'

const log = logger.createChildLogger({ name: 'PeerMediaDeviceContextSyncMonitorService' })

function makeMissingDeviceKey(sessionId: string, deviceId: string) {
    return `${sessionId}+${deviceId}`
}

type AddToMissingRemoteDevicesParams =
    | {
          deviceType: 'audio'
          map: Map<string, MissingRemoteDevice>
          peer: MusePeer
          info: AudioInputStreamInfo
      }
    | {
          deviceType: 'video'
          map: Map<string, MissingRemoteDevice>
          peer: MusePeer
      }

function addToMissingRemoteDevices(params: AddToMissingRemoteDevicesParams) {
    log.isVerbose() &&
        log.verbose('job', 'add to missing remote devices', {
            username: params.peer.username,
            userUrn: params.peer.userUrn,
            sessionId: params.peer.sessionId,
            deviceType: params.deviceType,
            info: params.deviceType === 'audio' ? params.info : undefined,
        })

    let key = ''
    let deviceId = ''
    let displayLabel = ''
    if (params.deviceType === 'audio') {
        deviceId = params.info.deviceId
        displayLabel = params.info.displayLabel
        key = makeMissingDeviceKey(params.peer.sessionId, params.info.deviceId)
    } else {
        key = makeMissingDeviceKey(params.peer.sessionId, params.deviceType)
    }

    if (!params.map.has(key)) {
        params.map.set(key, {
            sessionId: params.peer.sessionId,
            username: params.peer.username,
            deviceId: deviceId,
            displayLabel: displayLabel,
            deviceType: params.deviceType,
            addedTime: new Date(),
        })
    }
}

function removeFromMissingRemoteDevices(params: AddToMissingRemoteDevicesParams) {
    log.isVerbose() &&
        log.verbose('job', 'remove from missing remote devices', {
            username: params.peer.username,
            userUrn: params.peer.userUrn,
            sessionId: params.peer.sessionId,
            deviceType: params.deviceType,
            info: params.deviceType === 'audio' ? params.info : undefined,
        })

    let key = ''
    if (params.deviceType === 'audio') {
        key = makeMissingDeviceKey(params.peer.sessionId, params.info.deviceId)
    } else {
        key = makeMissingDeviceKey(params.peer.sessionId, params.deviceType)
    }

    params.map.delete(key)
}

export default function usePeerMediaDeviceContextSyncMonitorService() {
    const { roomStateRef } = useRoomContextRef()

    const missingRemoteDevicesRef = useRef<Map<string, MissingRemoteDevice>>(new Map())

    useEffect(function monitorPeerMediaDeviceMissing() {
        const checkFreqS = 5
        const notFoundThreshS = 15

        const interval = setInterval(() => {
            const room = roomStateRef.current
            if (!room) {
                return
            }

            log.verbose('job', 'starting.')

            Object.entries(room.peers).forEach(([_sessionId, peer]) => {
                // handle backward compatibility for older clients that don't properly clear `localSfuAudios`.
                const peerAppVersion = stripSemverVersion(peer.appVersion || '')
                const isLocalSfuAudiosCompatible = peerAppVersion && semver.gt(peerAppVersion, '1.5.2')

                // check there's a matching audio device in `remote(p2p/sfu)Audios`.
                for (let a of peer.manifest.localP2pAudios) {
                    let isMissing = peer.remoteAudios.find((b) => b.info.deviceId === a.info.deviceId) == null
                    if (isMissing) {
                        addToMissingRemoteDevices({
                            deviceType: 'audio',
                            map: missingRemoteDevicesRef.current,
                            peer: peer,
                            info: a.info,
                        })
                    } else {
                        removeFromMissingRemoteDevices({
                            deviceType: 'audio',
                            map: missingRemoteDevicesRef.current,
                            peer: peer,
                            info: a.info,
                        })
                    }
                }

                if (isLocalSfuAudiosCompatible) {
                    for (let a of peer.manifest?.localSfuAudios || []) {
                        let isMissing = peer.remoteAudios.find((b) => b.info.deviceId === a.info.deviceId) == null
                        log.verbose(
                            'job',
                            `(username=${peer.username}) isAudioDeprioritized=${peer.manifest.isAudioDeprioritized}`
                        )
                        if (isMissing && !peer.manifest.isAudioDeprioritized) {
                            addToMissingRemoteDevices({
                                deviceType: 'audio',
                                map: missingRemoteDevicesRef.current,
                                peer: peer,
                                info: a.info,
                            })
                        } else {
                            removeFromMissingRemoteDevices({
                                deviceType: 'audio',
                                map: missingRemoteDevicesRef.current,
                                peer: peer,
                                info: a.info,
                            })
                        }
                    }
                }

                // clear if gone from the peer manifest.
                missingRemoteDevicesRef.current.forEach((a, key) => {
                    if (a.deviceType !== 'audio') {
                        return
                    }
                    const b1 = !(peer.manifest?.localP2pAudios || []).find((b) => a.deviceId === b.info.deviceId)
                    const b2 = !(peer.manifest?.localSfuAudios || []).find((b) => a.deviceId === b.info.deviceId)
                    if (b1 && b2) {
                        missingRemoteDevicesRef.current.delete(key)
                    }
                })

                // check there's a matching video deivce in `remote (p2p/sfu) videos.`
                if (peer.manifest.localP2pVideo && peer.remoteVideo == null) {
                    addToMissingRemoteDevices({ deviceType: 'video', map: missingRemoteDevicesRef.current, peer })
                } else {
                    removeFromMissingRemoteDevices({ deviceType: 'video', map: missingRemoteDevicesRef.current, peer })
                }

                const isVideoVisible = videoSubscriptionManager.isVideoVisible(peer.sessionId)
                if (peer.manifest.localSfuVideo && peer.remoteVideo == null && isVideoVisible) {
                    addToMissingRemoteDevices({ deviceType: 'video', map: missingRemoteDevicesRef.current, peer })
                } else {
                    removeFromMissingRemoteDevices({ deviceType: 'video', map: missingRemoteDevicesRef.current, peer })
                }

                // clear if gone from the peer manifest.
                missingRemoteDevicesRef.current.forEach((a, key) => {
                    if (a.deviceType !== 'video') {
                        return
                    }
                    if (!peer.manifest.localP2pVideo && !peer.manifest.localSfuVideo) {
                        missingRemoteDevicesRef.current.delete(key)
                    }
                })
            })

            // remove items if the corresponding peer does not exist anymore.
            missingRemoteDevicesRef.current.forEach((entry, key) => {
                if (
                    room.peers[entry.sessionId] == null ||
                    room.peers[entry.sessionId].connectionStatus !== 'connected'
                ) {
                    missingRemoteDevicesRef.current.delete(key)
                }
            })

            // check items that have been gone for too long.
            let missingDevices: MissingRemoteDevice[] = []
            missingRemoteDevicesRef.current.forEach((entry, _key) => {
                const dts = (new Date().getTime() - entry.addedTime.getTime()) / 1000
                if (dts >= notFoundThreshS && !entry.logged) {
                    entry.logged = true
                    missingDevices.push(entry)
                }
            })

            if (missingDevices.length > 0) {
                log.error(
                    'expected_media_devices_not_found',
                    `listing expected devices in manifest ` + `not found in ${notFoundThreshS} seconds.`,
                    { list: missingDevices }
                )
            }

            log.verbose('job', 'finished.')
        }, checkFreqS * 1000)

        return () => {
            clearInterval(interval)
        }
    }, [])

    return null
}
